/**
 * PATH: packages/ui/src/components/email/signalTokens.ts
 * FILE_ID: UI-EMAIL-SIGNALTOKENS-001
 * PURPOSE: Semantic token contract for Email Triage Intelligence lanes.
 * WHY: Company / Classification / Intelligence / Filed to describe distinct
 *      downstream meanings while the stable internal lane keys preserve API
 *      and routing semantics;
 *      one visual grammar prevents the future triage strip becoming a
 *      flattened, unscannable chip palette.
 */

export const EMAIL_SIGNAL_LANES = ['who', 'what', 'signals', 'where'] as const
export type EmailSignalLane = (typeof EMAIL_SIGNAL_LANES)[number]

export type EmailSignalState = 'default' | 'suggested' | 'locked'

export interface EmailSignalLaneToken {
  label: string
  shortLabel: string
  /**
   * Caption colour for the fixed lane-label column.
   * Captions are field names — muted across all lanes so they do not compete
   * with value chips (reader expanded-context declutter, change #427).
   */
  labelClass: string
  chipClass: string
  suggestedClass: string
  lockedClass: string
}

/** Shared field-name caption — not a second status rainbow. */
export const EMAIL_SIGNAL_LANE_LABEL_CLASS = 'text-muted-foreground'

export const EMAIL_SIGNAL_LANE_TOKENS: Record<
  EmailSignalLane,
  EmailSignalLaneToken
> = {
  who: {
    label: 'Company',
    shortLabel: 'COMPANY',
    labelClass: EMAIL_SIGNAL_LANE_LABEL_CLASS,
    chipClass: 'bg-cyan-50 text-cyan-800 border-cyan-200 dark:bg-cyan-950/30 dark:text-cyan-200 dark:border-cyan-800',
    suggestedClass: 'border-cyan-300/70 bg-cyan-50/50 text-cyan-700 dark:border-cyan-700 dark:bg-cyan-950/20 dark:text-cyan-300',
    lockedClass: 'bg-cyan-100/60 text-cyan-800 border-cyan-300 dark:bg-cyan-950/50 dark:text-cyan-100 dark:border-cyan-700',
  },
  what: {
    label: 'Classification',
    // Compact strip labels must fit the fixed 88px lane column without
    // painting into the chip run (BUG-2026-07-20-EMAIL-SIGNAL-STRIP-OVERLAP).
    shortLabel: 'CLASS.',
    labelClass: EMAIL_SIGNAL_LANE_LABEL_CLASS,
    chipClass: 'bg-violet-50 text-violet-800 border-violet-200 dark:bg-violet-950/30 dark:text-violet-200 dark:border-violet-800',
    suggestedClass: 'border-violet-300/70 bg-violet-50/50 text-violet-700 dark:border-violet-700 dark:bg-violet-950/20 dark:text-violet-300',
    lockedClass: 'bg-violet-100/60 text-violet-800 border-violet-300 dark:bg-violet-950/50 dark:text-violet-100 dark:border-violet-700',
  },
  signals: {
    label: 'Intelligence',
    shortLabel: 'INTEL.',
    labelClass: EMAIL_SIGNAL_LANE_LABEL_CLASS,
    chipClass: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-800',
    suggestedClass: 'border-amber-300/70 bg-amber-50/50 text-amber-700 dark:border-amber-700 dark:bg-amber-950/20 dark:text-amber-300',
    lockedClass: 'bg-amber-100/60 text-amber-800 border-amber-300 dark:bg-amber-950/50 dark:text-amber-100 dark:border-amber-700',
  },
  where: {
    label: 'Filed to',
    shortLabel: 'FILED',
    labelClass: EMAIL_SIGNAL_LANE_LABEL_CLASS,
    chipClass: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-800',
    suggestedClass: 'border-emerald-300/70 bg-emerald-50/50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-300',
    lockedClass: 'bg-emerald-100/60 text-emerald-800 border-emerald-300 dark:bg-emerald-950/50 dark:text-emerald-100 dark:border-emerald-700',
  },
}

